import { IAppContext } from 'App/AppContext';
import { TFunction } from 'i18next';
import Joi from 'joi';
import { getLocalizedMessageOptions } from 'services/Localization/joiValidation';
import AppError from 'utils/appError';
import { areDifferent } from 'utils/array';
import Control from './control';
import GlobalDataCache from './globalDataCache/globalDataCache';
import Group from './group';
import { IOwner } from './owner';
import ResourceLink from './resourceLink';
import Risk_Translation from './risk_Translation';
import RiskMatrix from './RiskMatrix';
import { RiskStates } from './riskState';
import { RiskScoreDef } from './setting';
import User from './user';

export enum RiskMethod {
  Mitigate = 0,
  Avoid = 1,
  Transfer = 2,
  Accept = 3,
}

export default class Risk implements IOwner {
  riskId: number;

  authSchemaId?: number;

  riskStateId: number;

  code: string;

  externalId?: string;

  commentTrailId: number;

  auditTrailId: number;

  groupId?: string;

  group?: Group;

  ownerId?: string;

  ownerRoleId?: string;

  owner?: User;

  sortOrder: number;

  likelihoodStart: number;

  likelihoodCurrent: number;

  likelihoodGoal: number;

  impactStart: number;

  impactCurrent: number;

  impactGoal: number;

  dim3Start: number;

  dim3Current: number;

  dim3Goal: number;

  analysisTaskid?: number;

  method: RiskMethod;

  created?: Date;

  latestChangeDateTime?: Date;

  tagIds?: number[];

  normIds?: number[];

  trans: Risk_Translation[];

  controls?: Control[];

  resourceLinks: ResourceLink[];

  classificationGroupIds: number[];

  assetIds: number[];

  // field below are copied from the trans array for the current user language

  transIdx: number;

  name: string;

  asset: string;

  description: string;

  analysis: string;

  strategy: string;

  // internal props
  riskMatrix?: RiskMatrix;

  constructor() {
    this.riskId = -1;
    this.riskStateId = -1;
    this.commentTrailId = -1;
    this.auditTrailId = -1;
    this.sortOrder = -1;
    this.likelihoodStart = 0;
    this.likelihoodCurrent = 0;
    this.likelihoodGoal = 0;
    this.impactStart = 0;
    this.impactCurrent = 0;
    this.impactGoal = 0;
    this.dim3Start = 0;
    this.dim3Current = 0;
    this.dim3Goal = 0;
    this.trans = [];
    this.name = '';
    this.asset = '';
    this.description = '';
    this.analysis = '';
    this.strategy = '';
    this.code = '';
    this.method = RiskMethod.Mitigate;
    this.transIdx = -1;
    this.resourceLinks = [];
    this.classificationGroupIds = [];
    this.assetIds = [];
  }

  static getRiskMethodText = (method: RiskMethod, t: TFunction<string[]>): string => {
    switch (method) {
      case RiskMethod.Avoid:
        return t('risk:Method.Avoid');
      case RiskMethod.Mitigate:
        return t('risk:Method.Mitigate');
      case RiskMethod.Transfer:
        return t('risk:Method.Transfer');
      case RiskMethod.Accept:
        return t('risk:Method.Accept');
    }
  };

  static getRiskMethodFromText = (method: string | undefined, t: TFunction<string[]>): RiskMethod => {
    if (!method) return RiskMethod.Mitigate;
    switch (method.toLowerCase()) {
      case t('risk:Method.Avoid').toLowerCase():
        return RiskMethod.Avoid;
      case t('risk:Method.Mitigate').toLowerCase():
        return RiskMethod.Mitigate;
      case t('risk:Method.Transfer').toLowerCase():
        return RiskMethod.Transfer;
      case t('risk:Method.Accept').toLowerCase():
        return RiskMethod.Accept;
      default:
        return RiskMethod.Mitigate;
    }
  };

  getStartScore(): number {
    if (!this.riskMatrix) throw new AppError('RiskMatrix not set on Risk model when calculating start score');

    if (this.riskMatrix.enable3d) {
      return this.likelihoodStart * this.impactStart * this.dim3Start;
    } else {
      return this.likelihoodStart * this.impactStart;
    }
  }

  getCurrentScore(): number {
    if (!this.riskMatrix) throw new AppError('RiskMatrix not set on Risk model when calculating current score');

    if (this.riskMatrix.enable3d) {
      return this.likelihoodCurrent * this.impactCurrent * this.dim3Current;
    } else {
      return this.likelihoodCurrent * this.impactCurrent;
    }
  }

  setRiskMatrix(appContext: IAppContext) {
    const matrix = appContext.globalDataCache.settings.get(RiskScoreDef);
    if (matrix) {
      this.riskMatrix = matrix;
    }
  }

  getGoalScore(): number {
    if (!this.riskMatrix) throw new AppError('RiskMatrix not set on Risk model when calculating goal score');

    if (this.riskMatrix.enable3d) {
      return this.likelihoodGoal * this.impactGoal * this.dim3Goal;
    } else {
      return this.likelihoodGoal * this.impactGoal;
    }
  }

  static getScore(riskMatrix: RiskMatrix, likelihood: number, impact: number, dim3d: number): number {
    if (riskMatrix.enable3d) {
      return likelihood * impact * dim3d;
    } else {
      return likelihood * impact;
    }
  }

  clone(): Risk {
    const newItem = new Risk();
    newItem.riskId = this.riskId;
    newItem.riskStateId = this.riskStateId;
    newItem.code = this.code;
    newItem.externalId = this.externalId;
    newItem.commentTrailId = this.commentTrailId;
    newItem.auditTrailId = this.auditTrailId;
    newItem.groupId = this.groupId;
    newItem.ownerId = this.ownerId;
    newItem.sortOrder = this.sortOrder;
    newItem.likelihoodStart = this.likelihoodStart;
    newItem.likelihoodCurrent = this.likelihoodCurrent;
    newItem.likelihoodGoal = this.likelihoodGoal;
    newItem.impactStart = this.impactStart;
    newItem.impactCurrent = this.impactCurrent;
    newItem.impactGoal = this.impactGoal;
    newItem.dim3Start = this.dim3Start;
    newItem.dim3Current = this.dim3Current;
    newItem.dim3Goal = this.dim3Goal;
    newItem.analysisTaskid = this.analysisTaskid;
    newItem.trans = this.trans.map((trans) => trans.clone());
    newItem.transIdx = this.transIdx;
    newItem.name = this.name;
    newItem.asset = this.asset;
    newItem.description = this.description;
    newItem.group = this.group?.clone();
    newItem.analysis = this.analysis;
    newItem.method = this.method;
    newItem.strategy = this.strategy;
    newItem.controls = this.controls?.map((_control) => _control.clone());
    newItem.tagIds = this.tagIds ? [...this.tagIds] : undefined;
    newItem.classificationGroupIds = [...this.classificationGroupIds];
    newItem.assetIds = [...this.assetIds];
    newItem.resourceLinks = this.resourceLinks ? [...this.resourceLinks] : [];
    newItem.authSchemaId = this.authSchemaId;
    newItem.ownerRoleId = this.ownerRoleId;
    newItem.created = this.created ? new Date(this.created) : undefined;
    newItem.latestChangeDateTime = this.latestChangeDateTime ? new Date(this.latestChangeDateTime) : undefined;
    newItem.riskMatrix = this.riskMatrix;

    return newItem;
  }

  static getEmptyRisk(code: string, cache: GlobalDataCache): Risk {
    const output = new Risk();
    output.code = code;
    output.riskStateId = cache.riskStates.items[0]?.riskStateId || RiskStates.New;
    const trans = new Risk_Translation();
    output.trans.push(trans);
    output.transIdx = 0;

    return output;
  }

  // Validate function that validates the contents of the fields that have user input and can be written to the database
  // - Set abortEarly=false to make sure all errors are returned for the class
  // - Use getLocalizedMessageOptions() from the Localization service to get localized error messages
  // - The localizedFields array must be used to give each field in the error message a localized label
  validate(localizedFields: Record<string, string>): Joi.ValidationResult {
    const schema: Joi.ObjectSchema = Joi.object({
      code: Joi.string().max(32).required().label(localizedFields['code']),
      name: Joi.string().max(512).required().label(localizedFields['name']),
      externalId: Joi.string().allow('').max(256),
    }).prefs(getLocalizedMessageOptions());

    return schema.validate(
      { name: this.name, code: this.code, externalId: this.externalId?.trim() ?? '' },
      { abortEarly: false },
    );
  }

  isEqual(item: Risk): boolean {
    let isEqual: boolean = true;
    if (item.riskId !== this.riskId) isEqual = false;
    if (item.riskStateId !== this.riskStateId) isEqual = false;
    if (item.code !== this.code) isEqual = false;
    if (item.externalId !== this.externalId) isEqual = false;
    if (item.commentTrailId !== this.commentTrailId) isEqual = false;
    if (item.auditTrailId !== this.auditTrailId) isEqual = false;
    if (item.groupId !== this.groupId) isEqual = false;
    if (item.ownerId !== this.ownerId) isEqual = false;
    if (item.sortOrder !== this.sortOrder) isEqual = false;
    if (item.likelihoodStart !== this.likelihoodStart) isEqual = false;
    if (item.likelihoodCurrent !== this.likelihoodCurrent) isEqual = false;
    if (item.likelihoodGoal !== this.likelihoodGoal) isEqual = false;
    if (item.impactStart !== this.impactStart) isEqual = false;
    if (item.impactCurrent !== this.impactCurrent) isEqual = false;
    if (item.impactGoal !== this.impactGoal) isEqual = false;
    if (item.dim3Start !== this.dim3Start) isEqual = false;
    if (item.dim3Current !== this.dim3Current) isEqual = false;
    if (item.dim3Goal !== this.dim3Goal) isEqual = false;
    if (item.analysisTaskid !== this.analysisTaskid) isEqual = false;
    if (item.transIdx !== this.transIdx) isEqual = false;
    if (item.name !== this.name) isEqual = false;
    if (item.asset !== this.asset) isEqual = false;
    if (item.description !== this.description) isEqual = false;
    if (item.analysis !== this.analysis) isEqual = false;
    if (item.strategy !== this.strategy) isEqual = false;
    if (item.method !== this.method) isEqual = false;
    if (item.authSchemaId !== this.authSchemaId) isEqual = false;
    if (item.ownerRoleId !== this.ownerRoleId) return false;

    if (
      areDifferent(item.tagIds, this.tagIds, (a: number, b: number) => {
        return a === b;
      }) === true
    ) {
      isEqual = false;
    }

    if (
      areDifferent(item.classificationGroupIds, this.classificationGroupIds, (a: number, b: number) => {
        return a === b;
      }) === true
    ) {
      isEqual = false;
    }

    if (
      areDifferent(item.assetIds, this.assetIds, (a: number, b: number) => {
        return a === b;
      }) === true
    ) {
      isEqual = false;
    }

    if (
      areDifferent(item.controls, this.controls, (a: Control, b: Control) => {
        return a.controlId === b.controlId;
      }) === true
    ) {
      isEqual = false;
    }

    return isEqual;
  }
}
